import { API_BASE_URL } from '$lib/config';
import type { User } from '$lib/types';
import { resetBoardViewSession } from '$lib/utils/boardViewSession';

// teams' /auth/login and /auth/refresh return the refresh token in the JSON
// body (the purpose-scoped view-session cookie is not a refresh token), so unlike
// toyamacrm it has to be persisted client-side to survive a reload. It's a
// long-lived bearer credential, so localStorage (not sessionStorage) is the
// deliberate choice — same trust boundary as any SPA that keeps its refresh
// token client-side.
const REFRESH_KEY = 'teams_refresh_token';

// Anti-buddy-punching device binding: the server issues one device token per
// USER (not per browser) — see internal/api/handlers/devices.go. A shared
// office computer or a device someone else previously logged into on this
// browser must not silently hand its bound user's token to a different
// account, so tokens are keyed by the email being logged in and stored as a
// { email -> token } map under one localStorage key. Like the refresh token,
// this is never cleared on logout — only a successful login (for whichever
// email) may add/overwrite its own entry.
const DEVICE_TOKENS_KEY = 'teams_device_tokens';

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;
let currentDeviceToken: string | null = null;
let viewSessionClear: Promise<void> | null = null;
let authGeneration = 0;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

function setRefreshToken(token: string | null): void {
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

function getDeviceTokenMap(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(DEVICE_TOKENS_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function getStoredDeviceToken(email: string): string | undefined {
  return getDeviceTokenMap()[email.toLowerCase()];
}

function storeDeviceToken(email: string, token: string): void {
  const map = getDeviceTokenMap();
  map[email.toLowerCase()] = token;
  localStorage.setItem(DEVICE_TOKENS_KEY, JSON.stringify(map));
}

// getDeviceTokenHeader is attached to punch_in/punch_out requests only —
// every other endpoint stays JWT-only (see DESIGN). Empty object when there
// is no current device token (shouldn't normally happen post-login, but the
// server 403s with the standard mismatch message if it's missing/wrong).
export function getDeviceTokenHeader(): Record<string, string> {
  return currentDeviceToken ? { 'X-Device-Token': currentDeviceToken } : {};
}

// restoreDeviceToken re-hydrates the in-memory device token for `email` from
// localStorage. Called on page-reload bootstrap (after /auth/me resolves the
// logged-in user), since a silent refresh-token exchange never goes through
// login().
export function restoreDeviceToken(email: string): void {
  currentDeviceToken = getStoredDeviceToken(email) ?? null;
}

export interface AuthResult {
  status: 'ok';
  access_token: string;
  refresh_token: string;
  user: User;
  device_token?: string;
}

// MFAChallenge is what /auth/login returns for hr/admin accounts instead of
// tokens (see internal/api/handlers/auth.go's loginResult): mfa_token is a
// short-lived, purpose-scoped JWT that can only be exchanged at the
// matching mfa/setup* or mfa/verify endpoint below — it is never a valid
// bearer access token. device_token may still be set here: device
// resolution happens before the MFA branch, so a brand-new device can be
// registered on the very login attempt that also triggers a challenge.
export interface MFAChallenge {
  status: 'mfa_required' | 'mfa_setup_required';
  mfa_token: string;
  device_token?: string;
}

export type LoginOutcome = AuthResult | MFAChallenge;

export interface MFASetupInfo {
  otpauth_url: string;
  secret: string;
  // qr_data_url is a server-rendered PNG data URL (mirrors toyamacrm's QR
  // approach) — no frontend QR-rendering dependency needed.
  qr_data_url?: string;
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  // A quick re-login must not race a pending logout cookie deletion.
  if (viewSessionClear) await viewSessionClear.catch(() => {});
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  const data: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail =
      typeof data === 'object' && data !== null && 'detail' in data
        ? String((data as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || 'Request failed');
  }
  return data as T;
}

function finalizeAuth(data: AuthResult, newSession = true) {
  if (newSession) resetBoardViewSession();
  setAccessToken(data.access_token);
  setRefreshToken(data.refresh_token);
}

// applyDeviceToken persists a fresh device_token the moment it's seen —
// which can happen on the very login call that also returns an MFA
// challenge, before 2FA even completes.
function applyDeviceToken(email: string, fresh: string | undefined, existing: string | undefined) {
  currentDeviceToken = fresh || existing || null;
  if (currentDeviceToken) storeDeviceToken(email, currentDeviceToken);
}

export async function login(email: string, password: string): Promise<LoginOutcome> {
  const normalizedEmail = email.trim();
  const deviceToken = getStoredDeviceToken(normalizedEmail);
  const data = await postJSON<LoginOutcome>('/auth/login', {
    email: normalizedEmail,
    password,
    ...(deviceToken ? { device_token: deviceToken } : {})
  });
  applyDeviceToken(normalizedEmail, data.device_token, deviceToken);
  if (data.status === 'ok') finalizeAuth(data);
  return data;
}

// mfaSetupStart begins TOTP enrollment for an mfa_setup_required challenge:
// generates a fresh secret and returns the data needed to add it to an
// authenticator app.
export async function mfaSetupStart(mfaToken: string): Promise<MFASetupInfo> {
  return postJSON<MFASetupInfo>('/auth/mfa/setup_start', { mfa_token: mfaToken });
}

// mfaSetup confirms the first live code against the pending secret and
// completes the login (issues real tokens).
export async function mfaSetup(mfaToken: string, code: string): Promise<User> {
  const data = await postJSON<AuthResult>('/auth/mfa/setup', { mfa_token: mfaToken, code });
  finalizeAuth(data);
  return data.user;
}

// mfaVerify completes a login for an account with 2FA already confirmed.
export async function mfaVerify(mfaToken: string, code: string): Promise<User> {
  const data = await postJSON<AuthResult>('/auth/mfa/verify', { mfa_token: mfaToken, code });
  finalizeAuth(data);
  return data.user;
}

export async function refreshSession(): Promise<string> {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error('No refresh token'));
  }
  const generation = authGeneration;
  refreshPromise = (async () => {
    const data = await postJSON<AuthResult>('/auth/refresh', { refresh_token: refreshToken });
    if (generation !== authGeneration) throw new Error('Session ended during refresh');
    finalizeAuth(data, false);
    return data.access_token;
  })();
  try {
    return await refreshPromise;
  } catch (err) {
    setAccessToken(null);
    setRefreshToken(null);
    throw err;
  } finally {
    refreshPromise = null;
  }
}

export function logout(): Promise<void> {
  authGeneration++;
  setAccessToken(null);
  setRefreshToken(null);
  currentDeviceToken = null;
  resetBoardViewSession();
  // A pending refresh can itself set the HttpOnly cookie. Clear it only after
  // that response settles, and never let its tokens restore the logged-out SPA.
  const clearCookie = () => fetch(`${API_BASE_URL}/board/view-session`, {
    method: 'DELETE', credentials: 'same-origin'
  }).then((response) => {
    if (!response.ok) throw new Error('Could not clear notification tracking session.');
  });
  const request = refreshPromise ? refreshPromise.catch(() => {}).then(clearCookie) : clearCookie();
  viewSessionClear = request;
  return request.finally(() => {
    if (viewSessionClear === request) viewSessionClear = null;
  });
}

// ensureSession bootstraps a saved session on page load: if a refresh token
// is stored, exchange it for a fresh access token.
export async function ensureSession(): Promise<boolean> {
  if (accessToken) return true;
  if (!getRefreshToken()) return false;
  try {
    await refreshSession();
    return true;
  } catch {
    return false;
  }
}
